import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/apiBase";

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Fetches per-day free/total slot counts for a 31-day window starting
 * from `startDate` (default: today). Returns a Map<YYYY-MM-DD, {free,total,pct}>.
 * Used to color-code calendar dates:
 *   pct >= 75 → plenty   (full green)
 *   pct >= 1  → few      (half green / half red gradient)
 *   pct == 0  → booked   (light red, disabled)
 */
export function useDayAvailability(startDate) {
  const [byDate, setByDate] = useState(new Map());

  useEffect(() => {
    let cancelled = false;
    const base = new Date(startDate || new Date());
    base.setHours(0, 0, 0, 0);
    // Step back a few days so even past-week dates don't appear suddenly
    // uncoloured if the user navigates the month picker.
    const start = new Date(base);
    start.setDate(start.getDate() - 2);
    axios
      .get(
        `${API}/calendar/week-summary?start=${toDateStr(start)}&days=45`
      )
      .then((res) => {
        if (cancelled) return;
        const m = new Map();
        for (const d of res.data.days || []) {
          const pct = d.total > 0 ? Math.round((d.free / d.total) * 100) : 0;
          m.set(d.date, { free: d.free, total: d.total, pct });
        }
        setByDate(m);
      })
      .catch(() => {
        if (!cancelled) setByDate(new Map());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate ? toDateStr(new Date(startDate)) : ""]);

  /** Build react-day-picker modifier arrays from the fetched map. */
  const modifiers = {
    plenty: [],
    few: [],
    booked: [],
  };
  byDate.forEach((info, iso) => {
    const d = new Date(iso + "T00:00:00");
    if (info.pct === 0) modifiers.booked.push(d);
    else if (info.pct >= 75) modifiers.plenty.push(d);
    else modifiers.few.push(d);
  });

  return { byDate, modifiers };
}
